// The abstract `chrome-extension.auth` state's element, and the host chain above it (§1.3).
//
// Upstream's state declares `template: '<ui-view class="chrome-extension-auth"></ui-view>'`,
// and the whole auth stylesheet is scoped under that class — without the wrapper every
// ported rule misses and the screens render as unstyled HTML. That much was found by the
// pixel diff in the first Phase-2 pass.
//
// ⚠️ WHAT THE SECOND PASS FOUND: the wrapper alone is not enough, because THE ANCESTORS ARE
// LOAD-BEARING AND THEY ARE INLINE. Reading the real DOM of production at 200px wide:
//
//   body.chrome-extension.no-min-width      display=block  h=785px
//     ui-view.main                          display=inline h=auto
//       chrome-extension                    display=inline h=auto
//         ui-view                           display=inline h=auto
//           ui-view.chrome-extension-auth   display=flex   h=785px   ← height:100%
//
// Nothing styles those three, so as unknown elements they are `display: inline`. A
// percentage height resolves against the nearest BLOCK CONTAINER, and an inline box is not
// one — so `height: 100%` on the auth view skips all three and resolves against `body`,
// giving it the full 785px. Replace them with divs, as the first port did, and the nearest
// block container becomes a div of height `auto`: the same rule then computes 460px, and
// every flex child below it is laid out in a shorter box. The screens still look right; they
// are just wrong by a few percent everywhere, which is the expensive kind of wrong.
//
// So the chain is reproduced with the same tags upstream uses. They render as inline boxes
// here for exactly the same reason they do there.
import { createElement, type ReactElement, type ReactNode } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { ShortlistHeader } from '@woo-ext/ui'
import { useAuthGuard, type SessionState } from '@woo-ext/ui'
import type { ExtensionUser } from '@woo-ext/api'

/** What every auth route can read with `useOutletContext<AuthOutletContext>()`. */
export interface AuthOutletContext {
  /** Present only when the guard resolved an active session. */
  user?: ExtensionUser
  session: SessionState
}
import { useRouteChrome } from './useRouteChrome.ts'
import { useCloseDialog } from './useCloseDialog.ts'
import { SessionUnavailable } from './SessionUnavailable.tsx'
import { headerRuleFor, showsHeader } from './authHeader.ts'

/** `<ui-view>`, `<chrome-extension>` — unknown elements, and deliberately unstyled. */
function HostElement({ tag, className, children }: { tag: string; className?: string; children: ReactNode }): ReactElement {
  return createElement(tag, className === undefined ? null : { class: className }, children)
}

export function AuthLayout(): ReactElement {
  const { pathname } = useLocation()
  // The same answer `ShortlistHeader` computes for itself, read here because the DECISION to
  // render is upstream's `!extensionFrameService.isInIframe()` and belongs with the route.
  const inIframe = typeof window !== 'undefined' && window.self !== window.top
  const headerRule = headerRuleFor(pathname)

  // Applies the route's declared bodyClass — `no-min-width` on every auth screen, which is
  // what stops the main app's 1200px min-width applying inside a 200px iframe.
  useRouteChrome()
  // Present only inside a frame, which is also the condition the header checks — so the CTA
  // appears exactly where there is a parent to receive the `closeDialog`.
  const onClose = useCloseDialog()
  // And the route's declared auth guards, from the same table (§1.3). One place rather than
  // per route: upstream's are `$stateChangeStart` hooks, which is exactly a layout-level
  // concern, and a guard that each screen has to remember to apply is a guard that one of
  // them will not.
  const guard = useAuthGuard()

  return (
    <HostElement tag="ui-view" className="main">
      <HostElement tag="chrome-extension">
        <HostElement tag="ui-view">
          <HostElement tag="ui-view" className="chrome-extension-auth">
            {/* ⚠️ PER ROUTE, NOT ONCE FOR ALL OF THEM. An earlier version rendered this
                unconditionally here, under the comment "every auth state's template opens
                with this, identically" — which is false, and the twelve states disagree four
                ways. Seven render no header at all; `/auth/status` and the MFA parent render
                one only inside an iframe; `/auth/user` renders one without
                `hide-dynamic-ctas`. See authHeader.ts for the call sites.

                It survived because every screen with a signed-out reference happens to be one
                of the states that renders it unconditionally. The first signed-in baseline
                (`/auth/status`) put the diff at 8.69%: a 32px band and everything below it
                displaced. */}
            {showsHeader(pathname, inIframe) ? (
              <ShortlistHeader hideDynamicCtas={headerRule.hideDynamicCtas} onClose={onClose} />
            ) : null}
            {/* ⚠️ The outlet waits for the guard on a GUARDED route. Rendering regardless
                leaves the screen interactive for a round-trip — long enough for a signed-in
                user to submit `register3` from a sign-up form the guard is about to redirect
                away from. Upstream has no equivalent window: ui-router cancels the
                transition before the state is entered.

                An unguarded route is `allowed` on the first render with no request made, so
                `enable-cookies` and `out-of-date` appear immediately — they are what you see
                when the session cannot be established, and waiting on /user to show them
                would be exactly backwards. The header renders throughout either way, so the
                frame never flashes empty. */}
            {/* The user the guard already fetched, handed down rather than re-requested:
                StatusRoute and UserRoute both need it, and both used to call /user again —
                two sequential requests for one session, on the two screens hit most. */}
            {guard.status === 'allowed' ? (
              <Outlet context={{ user: guard.user, session: guard.session } satisfies AuthOutletContext} />
            ) : null}
            {guard.status === 'unavailable' ? <SessionUnavailable onRetry={guard.retry} /> : null}
          </HostElement>
        </HostElement>
      </HostElement>
    </HostElement>
  )
}
